/**
 * MobileWarning - Warning banner for mobile devices
 * @module components/starnexus/common/MobileWarning
 *
 */

import React from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Link from '@amzn/awsui-components-console/link';
import { useResponsive } from '../../../hooks/starnexus/useResponsive';

export interface MobileWarningProps {
  /** Optional custom message */
  message?: string;
  /** Whether to show the warning (defaults to auto-detect) */
  show?: boolean;
}

export const MobileWarning: React.FC<MobileWarningProps> = ({ message, show }) => {
  const { isMobile } = useResponsive();

  // Use prop if provided, otherwise auto-detect
  const shouldShow = show !== undefined ? show : isMobile;

  if (!shouldShow) {
    return null;
  }

  const defaultMessage = 'This application is optimized for desktop and tablet devices. Some features may be limited on mobile screens.';

  return (
    <div className="mobile-warning-banner" role="alert" aria-live="polite">
      <Alert type="warning" dismissible={false} header="Limited mobile experience">
        <Box variant="p">{message || defaultMessage}</Box>
        <Box variant="small" color="text-body-secondary" margin={{ top: 'xs' }}>
          For the best experience, please use a device with a larger screen.
        </Box>
      </Alert>
    </div>
  );
};

/**
 * MobileBlocker - Full-screen blocker for features disabled on mobile
 * Used for wizard and other complex features that don't work on mobile
 */
export interface MobileBlockerProps {
  /** Feature name being blocked */
  featureName: string;
  /** Redirect path */
  redirectPath?: string;
  /** Redirect label */
  redirectLabel?: string;
}

export const MobileBlocker: React.FC<MobileBlockerProps> = ({ featureName, redirectPath = '/starnexus/datasets', redirectLabel = 'Go to Datasets' }) => {
  const { isMobile } = useResponsive();

  if (!isMobile) {
    return null;
  }

  return (
    <Box textAlign="center" padding="xxl">
      <Alert type="warning" header={`${featureName} unavailable on mobile`}>
        <Box variant="p" margin={{ bottom: 'm' }}>
          The {featureName.toLowerCase()} requires a larger screen to function properly. Please access this feature from a desktop or tablet device.
        </Box>
        <Link href={redirectPath}>{redirectLabel}</Link>
      </Alert>
    </Box>
  );
};

export default MobileWarning;
